import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { createPlotlyChart } from 'src/app/_shared/plotlyConfig';
import { FleetGeneralFigureService } from 'src/app/_services/fleet-general-figure.service';
import { VehicleInfoService } from 'src/app/_services/vehicle-info.service';
import { VehicleCurrentInfo } from '../../../models/vehicle';
import { RbUiComponentsModule } from '@inst-iot/bosch-angular-ui-components'; 
import { KWhKmToMilesPipe } from '../../../pipes/k-wh-km-to-miles.pipe';
import { TransformXAxisPipe } from 'src/app/_shared/pipes/transform-xaxis.pipe';

@Component({
  selector: 'app-avg-ev-efficiency-chart-ef',
  standalone: true,
  imports: [CommonModule,RbUiComponentsModule],
  templateUrl: './avg-ev-efficiency-chart-ef.component.html',
  styleUrls: ['./avg-ev-efficiency-chart-ef.component.scss']
})
export class AvgEvEfficiencyChartEfComponent implements OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() selectedVehicles: any[] = [];
  @Input() fleetId: string[] = [];
  @Input() companyData: any;
  @Input() isMetric: boolean | undefined;
  currentVehicleData: any;
  errorMessage: boolean = false;
  dataLoading: boolean = false;
  vehiclesInfo!: { [key: string]: VehicleCurrentInfo; };
  chartRange!: { range: [number, number] };
  private readonly kWhKmToMilesPipe = new KWhKmToMilesPipe();

  constructor(
    private readonly fleetGeneralFigureService: FleetGeneralFigureService,
    private readonly vehicleInfoService: VehicleInfoService,
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataLoading = true;
    this.errorMessage = false;

    // Fetch fleet efficiency data
    this.fleetGeneralFigureService.getEnergyEfficiencyFleet(this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (datapoints) => {
        const fleetData = this.plotAverageFleetEfficiency(datapoints);
        this.fetchVehicleInfoAndUpdateChart(fleetData);
      },
      error: (error) => {
        console.error('Error fetching Energy Efficiency Fleet data:', error);
        this.dataLoading = false;
        this.errorMessage = true;
      }
    });
  }

  fetchVehicleInfoAndUpdateChart(fleetData: any[]): void {
    const deviceSerialNumber: string[] = this.selectedVehicles.map(vehicle => vehicle.batteryIdentifier);
    const vehicleNameMap: { [key: string]: string } = {};

    this.selectedVehicles.forEach(vehicle => {
      vehicleNameMap[vehicle.batteryIdentifier] = vehicle.name;
    });

    this.vehicleInfoService.getCurrentVehicleInfo(deviceSerialNumber).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (data) => {
        this.vehiclesInfo = data;

        const averageLines = deviceSerialNumber.map((id, index) => {
          const averageEVEfficiency = this.getConvertedEfficiencyValue(this.vehiclesInfo[id].avgEvEfficiency);
          const vehicleName = vehicleNameMap[id];

          const maxPercentAmount = Math.max(...fleetData.flatMap(plot => plot.y));

          return {
            x: [averageEVEfficiency, averageEVEfficiency],
            y: [0, maxPercentAmount],
            type: 'scatter',
            mode: 'lines',
            name: vehicleName,
            line: {
              color: this.getVehicleColor(index),
              width: 8
            }
          };
        });

        const combinedPlotData = [...fleetData, ...averageLines];
        this.updateChart(combinedPlotData);
        this.dataLoading = false;
      },
      error: (error) => {
        console.error('Error fetching Current Vehicle Info data:', error);
        this.dataLoading = false;
        this.errorMessage = true;
      }
    });
  }

  getVehicleColor(index: number): string {
    const colors = ['#007BC0', '#18837E','#9E2896'];
    return colors[index % 3];
  }

  plotAverageFleetEfficiency(datapoints: any): any[] {
    const transformXAxisPipe = new TransformXAxisPipe();
    return Object.keys(datapoints).flatMap((fleetId: string) => {
      const plotData = datapoints[fleetId];

      const energy = plotData.map((dataObject: any) => dataObject.binEnergy);
      const percentAmount = plotData.map((dataObject: any) => dataObject.percVeh);
      const convertedEnergy = this.getConvertedEfficiencyArray(energy);
      this.chartRange = transformXAxisPipe.transform(convertedEnergy, percentAmount, 5, 100);

      return {
        x: convertedEnergy,
        y: percentAmount,
        type: 'bar',
        name: `Fleet`,
        marker: {
          color: '#8A9097'
        }
      };
    });
  }

  updateChart(data: any[]): void {
    const layout = {
      title: 'Average EV Efficiency',
      font: {
        family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
        size: 13,
      },
      barmode: 'group',
      xaxis: {
        title: `Energy efficiency during driving [${this.getEfficiencyAxisLabel()}]`,
        range: this.chartRange?.range
      },
      yaxis: {
        title: 'Percentage of Fleet Vehicles [%]'
      },
      legend: {
        orientation: 'h',
        y: -0.3,
        xanchor: 'center',
        x: 0.5,
        font: {
          family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
          size: 13,
        },
      }
    };

    createPlotlyChart('average-ev-efficiency-chart', data, layout);
  }

  getConvertedEfficiencyArray(arr: number[]): number[] {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? arr : arr.map(val => this.kWhKmToMilesPipe.transform(val));
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? arr.map(val => this.kWhKmToMilesPipe.transform(val))
        : arr;
    }
  }

  getConvertedEfficiencyValue(val: number): number {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? val : this.kWhKmToMilesPipe.transform(val);
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? this.kWhKmToMilesPipe.transform(val)
        : val;
    }
  }

  getEfficiencyAxisLabel(): string {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric
        ? 'kWh/100km'
        : 'mi/kWh';
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? 'mi/kWh'
        : 'kWh/100km';
    }
  }
}
